import { BaseSyntheticEvent, useCallback, useEffect } from 'react'
import { GraphQLError } from 'graphql'
import { isString } from 'lodash'
import isEmpty from 'lodash/isEmpty'
import { useToastState } from 'transfix-ui/components/Toast'
import { IFileCreate } from 'types'
import { IDocumentInput, IError, IRateTypeKeyTypes } from 'types/graphqlTypes'
import { ISourcedCarrierRateInfoFragment } from '../graphql/SourcedCarrierRateInfoFragment'
import {
  CapacityType,
  TEditSourcedCarrierRateModalSchema,
} from './EditSourcedCarrierRateModalSchema'
import { useCreateSourcedCarrierRateMutation } from './graphql/CreateSourcedCarrierRate'
import { useUpdateSourcedCarrierRateMutation } from './graphql/UpdateSourcedCarrierRate'
import { useEditSourcedCarrierRateModalForm } from './useEditSourcedCarrierRateModalForm'

export const TOAST_MESSAGE = {
  create: {
    success: 'Successfully created sourced rate.',
    failure: 'Failed to create sourced rate: ',
  },
  update: {
    success: 'Successfully updated sourced rate.',
    failure: 'Failed to update sourced rate: ',
  },
}

const getResponseErrorMessages = (
  gqlErrors: readonly GraphQLError[] | undefined,
  submissionErrors: Pick<IError, 'message'>[] | undefined
): string[] => {
  const gqlErrorMessages = gqlErrors?.map(err => err.message) ?? []
  const submissionErrorMessages = submissionErrors?.map(err => err.message) ?? []

  return [...gqlErrorMessages, ...submissionErrorMessages]
}

const parseLaneAgreementsAsDocumentInput = (files: IFileCreate[]): IDocumentInput | null => {
  const [file] = files

  if (!isString(file?.file)) {
    return null
  }

  return {
    document: file.file,
    documentFileName: file.fileFileName,
  }
}

export interface IUseSourcedCarrierRateFormSubmitterResult {
  formMethods: ReturnType<typeof useEditSourcedCarrierRateModalForm>
  handleModalClose: () => void
  isFormValid: boolean
  loading: boolean
  submissionErrors?: string[]
  submitHandler: (e?: BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>
  toastState: Omit<ReturnType<typeof useToastState>, 'openToast'>
}

export const useSourcedCarrierRateFormSubmitter = (
  closeModal: () => void,
  shipperRateId: string,
  sourcedCarrierRate?: ISourcedCarrierRateInfoFragment
): IUseSourcedCarrierRateFormSubmitterResult => {
  const [
    createSourcedCarrierRateMutation,
    { data: createData, loading: createLoading },
  ] = useCreateSourcedCarrierRateMutation()

  const [
    updateSourcedCarrierRateMutation,
    { data: updateData, loading: updateLoading },
  ] = useUpdateSourcedCarrierRateMutation()

  const loading = createLoading || updateLoading

  const submissionErrors = sourcedCarrierRate
    ? updateData?.updateSourcedCarrierRate?.errors.map(err => err.message)
    : createData?.createSourcedCarrierRate?.errors.map(err => err.message)

  const { openToast, ...restToastState } = useToastState()

  const formMethods = useEditSourcedCarrierRateModalForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const { formState, handleSubmit, reset } = formMethods ?? {}
  const isFormValid = isEmpty(formState.errors)

  /**
   * @description Update the form's default values with the values of the Sourced Carrier Rate to be edited
   */
  useEffect(() => {
    if (sourcedCarrierRate) {
      const {
        canDoWeekends,
        carrierApplication,
        confirmed,
        maxCapacity,
        minCapacity,
        rate,
      } = sourcedCarrierRate

      reset({
        capacityType: minCapacity ? CapacityType.range : CapacityType.single,
        canDoWeekends: canDoWeekends ?? false,
        carrierApplicationId: carrierApplication?.id ?? '',
        confirmed: confirmed ?? false,
        laneAgreements: [],
        maxCapacity: maxCapacity ?? undefined,
        minCapacity: minCapacity ?? undefined,
        rate: {
          amount: rate.amount ?? undefined,
          typeKey: rate.rateTypeKey ?? '',
        },
      })
    }
  }, [reset, sourcedCarrierRate])

  const handleModalClose = useCallback(() => {
    // clear the form
    reset({
      capacityType: CapacityType.single,
      canDoWeekends: false,
      carrierApplicationId: '',
      confirmed: false,
      laneAgreements: [],
      maxCapacity: undefined,
      minCapacity: undefined,
      rate: {
        amount: undefined,
        typeKey: '',
      },
    })

    closeModal()
  }, [closeModal, reset])

  const createSourcedCarrierRate = useCallback(
    async (formData: TEditSourcedCarrierRateModalSchema) => {
      try {
        const { data, errors } = await createSourcedCarrierRateMutation({
          variables: {
            canDoWeekends: formData.canDoWeekends,
            carrierApplicationId: formData.carrierApplicationId,
            confirmed: formData.confirmed,
            laneAgreement: parseLaneAgreementsAsDocumentInput(formData.laneAgreements),
            maxCapacity: formData.maxCapacity,
            minCapacity: formData.capacityType === CapacityType.range ? formData.minCapacity : null,
            rate: {
              amount: formData.rate.amount,
              typeKey: formData.rate.typeKey as IRateTypeKeyTypes,
            },
            shipperRateId,
          },
        })

        const errorMessages = getResponseErrorMessages(
          errors,
          data?.createSourcedCarrierRate?.errors
        )

        if (errorMessages && errorMessages.length > 0) {
          openToast({
            toastMessage: `${TOAST_MESSAGE.create.failure}\n ${errorMessages.join('\n')}`,
            toastType: 'alert',
          })
        } else {
          handleModalClose()
          openToast({
            toastMessage: TOAST_MESSAGE.create.success,
          })
        }
      } catch (err) {
        openToast({
          toastMessage: `${TOAST_MESSAGE.create.failure}${err?.message}`,
          toastType: 'alert',
        })
      }
    },
    [createSourcedCarrierRateMutation, handleModalClose, openToast, shipperRateId]
  )

  const updateSourcedCarrierRate = useCallback(
    async (
      formData: TEditSourcedCarrierRateModalSchema,
      currentSourcedCarrierRate: ISourcedCarrierRateInfoFragment
    ) => {
      try {
        const { data, errors } = await updateSourcedCarrierRateMutation({
          variables: {
            id: currentSourcedCarrierRate.id,
            canDoWeekends: formData.canDoWeekends,
            carrierApplicationId: formData.carrierApplicationId,
            confirmed: formData.confirmed,
            laneAgreement: parseLaneAgreementsAsDocumentInput(formData.laneAgreements),
            maxCapacity: formData.maxCapacity,
            minCapacity: formData.capacityType === CapacityType.range ? formData.minCapacity : null,
            rate: {
              amount: formData.rate?.amount ?? currentSourcedCarrierRate.rate.amount,
              typeKey:
                (formData.rate?.typeKey as IRateTypeKeyTypes) ??
                currentSourcedCarrierRate.rate.rateTypeKey,
            },
          },
        })

        const errorMessages = getResponseErrorMessages(
          errors,
          data?.updateSourcedCarrierRate?.errors
        )

        if (errorMessages && errorMessages.length > 0) {
          openToast({
            toastMessage: `${TOAST_MESSAGE.update.failure}\n ${errorMessages.join('\n')}`,
            toastType: 'alert',
          })
        } else {
          handleModalClose()
          openToast({
            toastMessage: TOAST_MESSAGE.update.success,
          })
        }
      } catch (err) {
        openToast({
          toastMessage: `${TOAST_MESSAGE.update.failure}${err?.message}`,
          toastType: 'alert',
        })
      }
    },
    [handleModalClose, openToast, updateSourcedCarrierRateMutation]
  )

  const onSubmit = useCallback(
    async (data: TEditSourcedCarrierRateModalSchema) => {
      if (sourcedCarrierRate) {
        await updateSourcedCarrierRate(data, sourcedCarrierRate)
      } else {
        await createSourcedCarrierRate(data)
      }
    },
    [createSourcedCarrierRate, updateSourcedCarrierRate, sourcedCarrierRate]
  )

  return {
    formMethods,
    handleModalClose,
    isFormValid,
    loading,
    submissionErrors,
    submitHandler: handleSubmit(onSubmit),
    toastState: restToastState,
  }
}
